import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {Page} from '@PosterWhiteboard/page/page.class';

/**
 * Max time difference in seconds that the item playhead can drift from the page
 * clock before we force a seek.
 */
const MAXIMUM_SYNC_THRESHOLD = 0.5;

/**
 * Span times come from millisecond-grid quantized arithmetic, so an intended exact loop wrap can compute as a
 * hair above 0 or a hair below the stream duration instead of exactly 0. Well under a frame
 * period (~33ms), so no legitimate mid-stream frame falls inside it.
 */
const STREAM_WRAP_EPSILON = 0.001;

/**
 * Base class for items whose canvas state advances with the page clock — video
 * items, animated stickers, anything with a `play/pause/seek` notion. Owns the
 * page-clock subscription, the queue-of-one sync loop, and the play-state
 * reconciliation. Subclasses provide the medium-specific operations.
 */
export abstract class StreamingItem extends Item {
  #syncInFlight = false;
  #syncEnabled = true;
  #pendingPageTime: number | null = null;
  readonly #boundSyncToPage: (pageTime: number) => Promise<void>;

  protected constructor(page: Page) {
    super(page);
    this.#boundSyncToPage = this.#syncToPage.bind(this);
  }

  public override async onItemAddedToPage(): Promise<void> {
    this.page.on('time:updated', this.#boundSyncToPage);
    await this.seek(this.#getStreamTimeForPageTime(this.page.clock.getCurrentTime()));
    if (this.page.isPlaying) {
      await this.play();
    }
  }

  public override onRemove(): void {
    super.onRemove();
    this.page.off('time:updated', this.#boundSyncToPage);
  }

  public override isStreamingMediaItem(): boolean {
    return true;
  }

  /** Stop responding to page-clock ticks. Used by items embedded inside another
   * streaming item (e.g. slideshow video slides) where the parent drives sync. */
  public disableSync(): void {
    this.#syncEnabled = false;
  }

  // Medium-specific contract that concrete subclasses must implement. play /
  // pause / seek aren't listed here because Item already declares them as
  // overridable no-ops, and marking them abstract here would block subclasses
  // from calling `super.pause()` etc.
  public abstract getStreamDuration(): number;
  public abstract getCurrentTime(): number;
  public abstract stopItemStream(): Promise<void>;
  public abstract shouldPauseAtStreamEnd(): boolean;
  public abstract isStreamingItemPlaying(): boolean;

  // Queue-of-one: while a sync is in flight we just remember the latest pageTime
  // and re-enter the loop once the lock releases. Dropping intermediate ticks is
  // fine — only the latest matters — but the LAST tick must always fire, or the
  // item gets stuck at whatever state the most recent processed tick left it in.
  async #syncToPage(pageTime: number): Promise<void> {
    if (!this.#syncEnabled) {
      return;
    }
    this.#pendingPageTime = pageTime;
    if (this.#syncInFlight) {
      return;
    }

    this.#syncInFlight = true;
    try {
      // Sequential by design: each iteration must complete before we re-read
      // pendingPageTime, otherwise concurrent ticks would race the seek/play
      // state and produce inconsistent playback. Parallelising defeats the
      // queue-of-one — disable no-await-in-loop here.
      while (this.#pendingPageTime !== null) {
        const t = this.#pendingPageTime;
        this.#pendingPageTime = null;
        // eslint-disable-next-line no-await-in-loop
        await this.#reconcileItemTime(t);
        // eslint-disable-next-line no-await-in-loop
        await this.#reconcilePlaybackState();
      }
    } finally {
      this.#syncInFlight = false;
    }
  }

  async #reconcileItemTime(pageTime: number): Promise<void> {
    if (pageTime >= 0 && pageTime <= this.page.getDuration()) {
      const relativeReferenceTime = this.#getStreamTimeForPageTime(pageTime);
      const currentTime = this.getCurrentTime();

      if (Math.abs(relativeReferenceTime - currentTime) > MAXIMUM_SYNC_THRESHOLD) {
        await this.seek(relativeReferenceTime);
      }
    } else {
      await this.stopItemStream();
    }
  }

  /**
   * Page-seconds into the loop cycle at which the stream's first frame starts.
   * Looping subclasses with a phase offset (VideoItem's loopStartOffset) override
   * this; only the wrapping branch of the page-time mapping consumes it.
   */
  protected getLoopPhaseOffsetPageSeconds(): number {
    return 0;
  }

  // The stream's local time base is pageTime - onPageStartTime, not pageTime —
  // an item slotted mid-timeline (e.g. the second of two sequential video bgs)
  // starts its media at 0 when the page clock reaches its slot. Looping streams
  // wrap around the stream duration, phase-shifted by the loop offset so a bar
  // can start mid-pass; pause-at-end streams hold (the media element clamps
  // past its end).
  #getStreamTimeForPageTime(pageTime: number): number {
    const streamTime = Math.max(0, pageTime - this.onPageStartTime);
    if (this.shouldPauseAtStreamEnd()) {
      return streamTime;
    }
    if (this.#shouldHoldLastFrameForOutTransition(pageTime)) {
      return this.#getStreamTimeAtSpanEnd();
    }
    return (streamTime + this.getLoopPhaseOffsetPageSeconds()) % this.getStreamDuration();
  }

  // The frame showing at the item's span end — what the out-transition hold must freeze.
  // Only looping streams reach this (pause-at-end returns earlier), and their boundary
  // rarely lands on a loop end: the visible frame there is (span + phase) % duration, not
  // the stream's final frame. An exact-wrap boundary (% yields 0) holds the last frame,
  // since that's the frame being approached.
  #getStreamTimeAtSpanEnd(): number {
    const spanLength = Math.max(0, this.getPageEndTime() - this.onPageStartTime);
    const streamDuration = this.getStreamDuration();
    const wrapped = (spanLength + this.getLoopPhaseOffsetPageSeconds()) % streamDuration;
    if (wrapped < STREAM_WRAP_EPSILON || streamDuration - wrapped < STREAM_WRAP_EPSILON) {
      return streamDuration;
    }
    return wrapped;
  }

  // During the second half of this item's out-transition the page time is already past its
  // span, but the item is still visible blending out — the modulo above would wrap the stream
  // near its start and yank the visible frame. Hold the last frame instead.
  #shouldHoldLastFrameForOutTransition(pageTime: number): boolean {
    const outTransition = this.page.transitions.getTransitionOutForItem(this.uid);
    if (!outTransition) {
      return false;
    }
    const window = this.page.transitions.getWindowForTransition(outTransition);
    return window !== null && pageTime >= window.boundaryTime && pageTime < window.endTime;
  }

  async #reconcilePlaybackState(): Promise<void> {
    const shouldPlay = this.#shouldItemBePlaying();
    const isPlaying = this.isStreamingItemPlaying();
    if (shouldPlay && !isPlaying) {
      await this.play();
    } else if (!shouldPlay && isPlaying) {
      await this.pause();
    }
  }

  #shouldItemBePlaying(): boolean {
    if (!this.page.isPlaying) {
      return false;
    }
    const pageTime = this.page.clock.getCurrentTime();
    return pageTime >= this.onPageStartTime && pageTime < this.getPageEndTime();
  }
}
